import React, { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from './ui/Modal';
import { toaster } from './ui/toast';
import { FormattedPhoneInput } from './FormattedPhoneInput';
import { useUserTypes } from '../hooks/useUserTypes';
import { useEstatesWithGroups } from '../hooks/useEstates';
import { createResident, createResidentFailure } from '../api/frontDeskUsers';
import {
  EMPTY_RESIDENT, residentFailureMessage, residentIssues, residentRequest, residentialUserTypes, type ResidentForm, type ResidentIssue,
} from '../pages/Users/residentForm';

const DEFAULT_COUNTRY = { id: 'DO', label: 'Dominican Republic', dialCode: '+1' } as React.ComponentProps<typeof FormattedPhoneInput>['country'];
const MAX_ESTATE_OPTIONS = 80;

const ISSUE_TEXT: Record<ResidentIssue, string> = {
  email: 'Enter a valid email.',
  name: 'Enter the first name.',
  family_name: 'Enter the last name.',
  user_type: 'Pick the type of resident.',
  estate: 'Pick the residence.',
};

/**
 * Front Desk: add an owner, a resident or a property manager (POST /sam/users). No password: the person gets
 * Cognito's invitation by email. The residence is required and the link to it is created approved.
 */
export const CreateResidentSheet: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const { userTypes } = useUserTypes();
  const { data: estates } = useEstatesWithGroups();
  const [form, setForm] = useState<ResidentForm>(EMPTY_RESIDENT);
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [estateSearch, setEstateSearch] = useState('');
  const [showIssues, setShowIssues] = useState(false);

  const types = useMemo(() => residentialUserTypes(userTypes), [userTypes]);
  const estateOptions = useMemo(() => {
    const q = estateSearch.trim().toLowerCase();
    const all = (Array.isArray(estates) ? estates : []) as Array<{ id: number | string; name?: string; estate_group?: { name?: string } }>;
    const label = (e: (typeof all)[number]) => `${e.name ?? e.id}${e.estate_group?.name ? ` — ${e.estate_group.name}` : ''}`;
    return all.filter((e) => !q || label(e).toLowerCase().includes(q)).slice(0, MAX_ESTATE_OPTIONS).map((e) => ({ id: String(e.id), label: label(e) }));
  }, [estates, estateSearch]);

  const issues = residentIssues(form);
  const set = <K extends keyof ResidentForm>(k: K, v: ResidentForm[K]) => setForm((p) => ({ ...p, [k]: v }));

  const close = () => {
    setForm(EMPTY_RESIDENT);
    setCountry(DEFAULT_COUNTRY);
    setEstateSearch('');
    setShowIssues(false);
    onClose();
  };

  const create = useMutation({
    mutationFn: () => createResident(residentRequest(form)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user-estates'] });
      toaster.positive('Resident created. They will receive an invitation by email.', { autoHideDuration: 4000 });
      close();
    },
    onError: (err) => {
      toaster.negative(residentFailureMessage(createResidentFailure(err)), { autoHideDuration: 6000 });
    },
  });

  const submit = () => {
    if (issues.length > 0) { setShowIssues(true); return; }
    create.mutate();
  };

  const err = (k: ResidentIssue) => (showIssues && issues.includes(k) ? ISSUE_TEXT[k] : undefined);

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      isDrawer
      title="Add resident"
      subtitle="Owner, resident or property manager"
      footer={(
        <div className="flex items-center justify-between w-full gap-2">
          <button type="button" className="btn ghost" onClick={close} disabled={create.isPending}>Cancel</button>
          <button type="button" className="btn primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create resident'}
          </button>
        </div>
      )}
    >
      <div className="fc" style={{ marginBottom: 12 }}>
        <label htmlFor="cr-email">Email <span className="req">*</span></label>
        <input id="cr-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="name@example.com" />
        {err('email') && <div className="msg" role="alert">{err('email')}</div>}
      </div>
      <div className="grid grid-cols-2 gap-x-3">
        <div className="fc" style={{ marginBottom: 12 }}>
          <label htmlFor="cr-name">First name <span className="req">*</span></label>
          <input id="cr-name" value={form.name} onChange={(e) => set('name', e.target.value)} />
          {err('name') && <div className="msg" role="alert">{err('name')}</div>}
        </div>
        <div className="fc" style={{ marginBottom: 12 }}>
          <label htmlFor="cr-family">Last name <span className="req">*</span></label>
          <input id="cr-family" value={form.family_name} onChange={(e) => set('family_name', e.target.value)} />
          {err('family_name') && <div className="msg" role="alert">{err('family_name')}</div>}
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <div className="text-[13px] font-semibold mb-1.5" style={{ color: 'var(--ink-700)' }}>Phone (optional)</div>
        <FormattedPhoneInput
          value={form.phone_number}
          country={country}
          onChange={(value, c) => { set('phone_number', value); setCountry(c); }}
          placeholder="Phone number"
          showFormatHint={false}
        />
      </div>
      <div className="fc" style={{ marginBottom: 12 }}>
        <label htmlFor="cr-type">Type <span className="req">*</span></label>
        <select id="cr-type" value={form.user_type_id} onChange={(e) => set('user_type_id', e.target.value)}>
          <option value="">Select a type</option>
          {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {err('user_type') && <div className="msg" role="alert">{err('user_type')}</div>}
      </div>
      <div className="fc" style={{ marginBottom: 12 }}>
        <label htmlFor="cr-estate-search">Residence <span className="req">*</span></label>
        <input id="cr-estate-search" value={estateSearch} onChange={(e) => setEstateSearch(e.target.value)} placeholder="Search by name or community" />
        <select aria-label="Residence" value={form.estate_id} onChange={(e) => set('estate_id', e.target.value)} style={{ marginTop: 6 }} size={Math.min(6, Math.max(2, estateOptions.length + 1))}>
          <option value="">Select a residence</option>
          {estateOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
        {err('estate') && <div className="msg" role="alert">{err('estate')}</div>}
      </div>
    </Modal>
  );
};
